import type { ListBoxItemProps as AriaListBoxItemProps } from "react-aria-components";

import { Check } from "@untitledui/icons";
import { isValidElement, useContext } from "react";
import {
	ListBoxItem as AriaListBoxItem,
	Text as AriaText,
} from "react-aria-components";

import { Avatar } from "@/components/ui/avatar/avatar";
import { cx } from "@/utils/cx";
import { isReactComponent } from "@/utils/is-react-component";

import type { SelectItemType } from "./select";

import { SelectContext } from "./select-context";

const sizes = {
	md: "p-2.5 pl-2",
	sm: "p-2 pr-2.5",
};

interface SelectItemProps
	extends Omit<AriaListBoxItemProps<SelectItemType>, "id">, SelectItemType {}

export const SelectItem = ({
	avatarUrl,
	children,
	className,
	description,
	icon: Icon,
	id,
	isDisabled,
	label,
	supportingText,
	value,
	...props
}: SelectItemProps) => {
	const { size, variant } = useContext(SelectContext);
	const isPlain = variant === "plain";

	const labelOrChildren =
		label || (typeof children === "string" ? children : "");
	const textValue = supportingText
		? labelOrChildren + " " + supportingText
		: labelOrChildren;

	return (
		<AriaListBoxItem
			id={id}
			isDisabled={isDisabled}
			textValue={textValue}
			value={
				value ?? {
					avatarUrl,
					description,
					icon: Icon,
					id,
					isDisabled,
					label: labelOrChildren,
					supportingText,
				}
			}
			{...props}
			className={(state) =>
				cx(
					"w-full px-1.5 py-px outline-hidden",
					typeof className === "function" ? className(state) : className,
				)
			}
		>
			{(state) => (
				<div
					className={cx(
						"flex cursor-pointer items-center gap-2 rounded-md outline-hidden select-none",
						state.isSelected && "bg-primary_hover",
						state.isDisabled && "cursor-not-allowed opacity-50",
						state.isFocused && "bg-primary_hover",
						state.isFocusVisible && "ring-2 ring-focus-ring ring-inset",

						// Icon styles
						"*:data-icon:shrink-0 *:data-icon:text-fg-quaternary",
						isPlain ? "*:data-icon:size-4" : "*:data-icon:size-5",

						isPlain ? "px-2 py-1.5" : sizes[size],
					)}
				>
					{avatarUrl ? (
						<Avatar alt={label} aria-hidden="true" size="xs" src={avatarUrl} />
					) : isReactComponent(Icon) ? (
						<Icon aria-hidden="true" data-icon />
					) : isValidElement(Icon) ? (
						Icon
					) : null}

					<div className="flex w-full min-w-0 flex-1 flex-wrap gap-x-2">
						<AriaText
							className={cx(
								"truncate whitespace-nowrap text-primary",
								isPlain ? "text-sm font-semibold" : "text-md font-medium",
							)}
							slot="label"
						>
							{label ||
								(typeof children === "function" ? children(state) : children)}
						</AriaText>

						{supportingText && (
							<AriaText
								className={cx(
									"whitespace-nowrap text-tertiary",
									isPlain ? "text-sm" : "text-md",
								)}
								slot="description"
							>
								{supportingText}
							</AriaText>
						)}
						{description && (
							<AriaText
								className={cx(
									"block w-full text-sm whitespace-normal text-tertiary",
								)}
								slot="description"
							>
								{description}
							</AriaText>
						)}
					</div>

					{state.isSelected && (
						<Check
							aria-hidden="true"
							className={cx(
								"ml-auto text-fg-brand-primary",
								isPlain || size === "sm" ? "size-4 stroke-[2.5px]" : "size-5",
							)}
						/>
					)}
				</div>
			)}
		</AriaListBoxItem>
	);
};
